import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { createStepComponent } from '@flipster/react/misc/step/Steps';
import { useSteps } from '@flipster/react/misc/step/use-steps';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { getErrorMessage } from '@flipster/utils/error';
import segment from '@flipster/utils/segment';
import { CommunityEvent } from '@flipster/utils/segment/events/community';

import { SegmentPage } from '../../../../misc/segment/segment-page';
import { tradingLeaderboardMyRankingBunja } from '../../../../trading-leaderboard-page/bunja';
import { followOnboardingBunja } from './bunja';
import { FollowCard } from './follow-card';
import followOnboardingImage from './follow-onboarding-image.png';

type FollowOnboardingModel = {
  start: undefined;
  suggestion: undefined;
};
const Steps = createStepComponent<FollowOnboardingModel>();

export const FollowOnboardingModal = () => {
  const { current, push } = useSteps<FollowOnboardingModel>({ initialValue: { step: 'start', context: undefined } });
  return (
    <>
      <SegmentPage name={CommunityEvent.TopTradersModalView} />
      <Modal size='large'>
        <Steps model={current}>
          <Steps.Step step='start'>
            {() => (
              <StartStep
                handleContinueClick={() => {
                  segment.track(CommunityEvent.TopTradersModalContinueClick);
                  push({ step: 'suggestion', context: undefined });
                }}
              />
            )}
          </Steps.Step>
          <Steps.Step step='suggestion'>{() => <FollowSuggestionStep />}</Steps.Step>
        </Steps>
      </Modal>
    </>
  );
};

interface StartStepProps {
  handleContinueClick: () => void;
}
const StartStep = ({ handleContinueClick }: StartStepProps) => {
  return (
    <>
      <Modal.Header>
        <Modal.Title label='See who’s winning' />
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          Follow top traders and see how they trade in real time.
        </p>
      </Modal.Header>
      <Modal.Content>
        <img
          className={css({ w: '100%', objectFit: 'cover', tablet: { px: 100, py: 60 } })}
          src={followOnboardingImage.src}
          alt='Flipster app notification showing a top trader just closed a long BTC position, with a hand tapping the alert.'
        />
      </Modal.Content>
      <Modal.Footer>
        <Button label='Continue' onClick={handleContinueClick} />
      </Modal.Footer>
    </>
  );
};

export const FollowSuggestionStep = () => {
  const { onClose } = useOverlayItem();
  const { myIntervalRankingsAtom } = useBunja(tradingLeaderboardMyRankingBunja);
  const myIntervalRankings = useAtomValue(myIntervalRankingsAtom);
  const { followSuggestionsAtom, completeOnboardingModal } = useBunja(followOnboardingBunja);
  async function completeAndClose() {
    try {
      await completeOnboardingModal();
      onClose();
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  }
  const followSuggestions = useAtomValue(followSuggestionsAtom);
  return (
    <>
      <Modal.Header>
        <Modal.Title label='Instant alerts from top traders' />
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          Get alerts when they open or close a trade.
        </p>
      </Modal.Header>
      <Modal.Content css={{ tabletDown: { pb: 0 } }}>
        <div className={stack({ gap: 12, maxH: '100%', overflowY: 'scroll' })}>
          {followSuggestions.map((suggestion) => (
            <FollowCard key={suggestion.profileId} suggestion={suggestion} />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className={vstack({ gap: 16 })}>
          <div className={center({ direction: 'row', gap: 8, color: 'content.default.level-1' })}>
            <InformationCircleIcon size={16} className={css({ flexShrink: 0 })} />
            <p className={css({ textStyle: 'Text/M/Regular' })}>
              When you follow others, your trading activity and performance become visible to others.
            </p>
          </div>
          <div className={hstack({ gap: 8, tabletDown: { w: '100%' } })}>
            <Button
              hierarchy='secondary'
              color='neutral'
              size='large'
              label='Skip for now'
              onClick={completeAndClose}
              css={{ tabletDown: { flex: 1 }, tablet: { w: 180 } }}
            />
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              label='View more traders'
              as='a'
              href={`/trading-leaderboard?tier=${myIntervalRankings.WEEKLY?.tier ?? 'ALL'}&interval=WEEKLY`}
              onClick={() => {
                segment.track(CommunityEvent.TopTradersModalMoreClick);
                completeAndClose();
              }}
              css={{ tabletDown: { flex: 1 }, tablet: { w: 180 } }}
            />
          </div>
        </div>
      </Modal.Footer>
    </>
  );
};
